import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import usePageMeta from "@/hooks/usePageMeta";
import { getFilmById, getSpecialById, specialList, typeLabels } from "@/lib/films";

export default function SpecialPage() {
  const { id } = useParams();
  const special = getSpecialById(id);

  usePageMeta({
    title: special ? `${special.title} - 影评专题 - 人兽影视网` : "未找到该专题 - 人兽影视网",
    description: special
      ? `${special.intro.slice(0, 70)}人兽影视网影评专题，聚合相关作品与深度解读。`
      : "该影评专题可能已下线，请返回人兽影视网浏览其他专题策划。",
    keywords: special
      ? `${special.title},影评专题,影视解读,深度影评,人兽影视网`
      : "影评专题,影视解读,人兽影视网",
  });

  if (!special) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="container-portal flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-article-line text-2xl" />
          </span>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">未找到该影评专题</h1>
          <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
            专题可能已下线或链接有误。你也可以回到首页，或直接浏览站内影视作品。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
            <Link
              to="/category/tv"
              className="inline-flex items-center rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
            >
              浏览电视剧
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = specialList.filter((item) => special.related.includes(item.id));

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <section className="relative overflow-hidden bg-primary-950">
          <img
            src={special.cover}
            alt={`${special.title} 专题封面`}
            title={`${special.title} 影评专题`}
            className="absolute inset-0 h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-900/45" />
          <div className="container-portal relative py-10 md:py-16">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-300/75">
              <Link to="/" className="transition-colors hover:text-accent-300 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span>专题</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-100">{special.subtitle}</span>
            </nav>

            <span className="label-caps mt-6 inline-block text-[11px] text-accent-300">
              {special.subtitle}
            </span>
            <h1 className="mt-3 max-w-3xl text-2xl font-black leading-tight tracking-tight text-background-50 md:text-[40px]">
              {special.title}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-background-200/85">{special.intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] text-background-300/80">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                人兽影视编辑部
              </span>
              <span className="h-3 w-px bg-background-50/20" />
              <span>{special.groups.reduce((total, group) => total + group.entries.length, 0)} 篇影评</span>
              <span className="h-3 w-px bg-background-50/20" />
              <span>2026 年秋季策划</span>
            </div>
          </div>
        </section>

        <div className="container-portal mt-8 grid grid-cols-1 gap-7 pb-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-7">
            {special.groups.map((group) => (
              <section key={group.name} aria-labelledby={`group-${group.name}`}>
                <div className="flex items-center gap-3">
                  <h2 id={`group-${group.name}`} className="text-lg font-bold text-foreground-950 md:text-xl">
                    {group.name}
                  </h2>
                  <span className="h-px flex-1 bg-background-200" />
                  <span className="text-[11px] text-foreground-500">{group.entries.length} 篇</span>
                </div>

                <div className="mt-4 space-y-3">
                  {group.entries.map((entry) => {
                    const film = getFilmById(entry.filmId);
                    if (!film) return null;
                    return (
                      <article
                        key={entry.title}
                        className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5"
                      >
                        <div className="flex gap-4">
                          <Link
                            to={`/detail/${film.id}`}
                            className="h-[92px] w-[62px] shrink-0 overflow-hidden rounded-md bg-secondary-900 cursor-pointer"
                          >
                            <img
                              src={film.poster}
                              alt={`${film.title} 海报`}
                              title={`${film.title} 剧情与影评`}
                              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            />
                          </Link>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                                {typeLabels[film.type]}
                              </span>
                              <Link
                                to={`/detail/${film.id}`}
                                className="text-primary-700 transition-colors hover:text-accent-600 cursor-pointer"
                              >
                                {film.title}
                              </Link>
                              <span>·</span>
                              <span>{film.year}</span>
                            </div>
                            <h3 className="mt-1.5">
                              <Link
                                to={`/detail/${film.id}#reviews`}
                                className="text-[15px] font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 cursor-pointer"
                              >
                                {entry.title}
                              </Link>
                            </h3>
                            <p className="mt-1.5 text-[13px] leading-6 text-foreground-600">
                              {entry.excerpt}
                            </p>
                            <p className="mt-2 flex items-center gap-2 text-[11px] text-foreground-500">
                              <span>{entry.author}</span>
                              <span className="h-2.5 w-px bg-background-300" />
                              <span>专题约稿</span>
                            </p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}

            <section className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
              <div className="flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-600" />
                <h2 className="text-base font-bold text-accent-900">为什么推荐这一期</h2>
              </div>
              <p className="mt-3 text-[13.5px] leading-7 text-accent-900/85">{special.reason}</p>
            </section>
          </div>

          <aside className="space-y-5">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-bold text-foreground-950">本期片单</h2>
              <ul className="mt-3 space-y-2.5">
                {special.groups
                  .flatMap((group) => group.entries)
                  .map((entry) => {
                    const film = getFilmById(entry.filmId);
                    if (!film) return null;
                    return (
                      <li key={`${entry.filmId}-list`}>
                        <Link
                          to={`/detail/${film.id}`}
                          className="flex items-center justify-between gap-3 rounded-md bg-background-100/70 px-3 py-2 text-xs text-foreground-800 transition-colors hover:bg-background-200/70 cursor-pointer"
                        >
                          <span className="truncate">{film.title}</span>
                          <span className="flex shrink-0 items-center gap-1 text-[11px] text-accent-600">
                            <i className="ri-star-fill text-[10px]" />
                            {film.rating.toFixed(1)}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-sm font-bold text-foreground-950">相关专题</h2>
              <div className="mt-3 space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/special/${item.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <span className="h-[54px] w-[80px] shrink-0 overflow-hidden rounded-md bg-secondary-900">
                      <img
                        src={item.cover}
                        alt={`${item.title} 专题封面`}
                        title={item.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-[12.5px] font-medium leading-5 text-foreground-900 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[10px] text-foreground-500">{item.subtitle}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            <div className="flex flex-wrap gap-x-4 gap-y-2 px-1 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
                <i className="ri-home-4-line mr-1" />
                返回首页
              </Link>
              <Link to="/category/movie" className="transition-colors hover:text-primary-600 cursor-pointer">
                <i className="ri-film-line mr-1" />
                浏览电影
              </Link>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}